export const STYLES = `
/* ---- Design tokens ----
 * Single source of truth for colour, motion and radius. Everything else
 * in this stylesheet leans on these so a future re-skin is a top-of-file
 * change, not a find-replace pass.
 */
:host {
  all: initial;
  /* Match Capsiynau's Tailwind default font-sans stack exactly so the widget
   * font renders identically to the capsiynau.com pages. The previous stack
   * included "Inter" which hijacked rendering on dev machines that have
   * Inter installed (most Macs via Homebrew) - Capsiynau stayed in SF Pro
   * via -apple-system / BlinkMacSystemFont, so the two surfaces visibly
   * diverged. */
  font-family: ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont,
               "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
  color: #f1f5f9;
  color-scheme: dark;

  /* Brand accent — violet → indigo gradient. Used on active pill, primary
   * highlight, focus rings, toggles. The flat fallback is for browsers
   * without gradient support and for solid-colour spots (focus outline). */
  --lw-accent-start: #a78bfa;       /* violet-400 */
  --lw-accent-end:   #818cf8;       /* indigo-400 */
  --lw-accent-flat:  #a78bfa;
  --lw-accent-soft:  rgba(167, 139, 250, 0.18);

  /* Surfaces — Capsiynau dark family palette (slate-950 ground, slate-900
   * cards, slate-800 borders). Force-dark on every host page so the
   * widget reads as Llais brand regardless of the page underneath - the
   * landing page is dark, the developer dashboard surfaces are dark,
   * and the widget needs to match. */
  --lw-surface:        rgba(15, 23, 42, 0.94);   /* slate-900 @ 94% */
  --lw-surface-solid:  #0f172a;                  /* slate-900 */
  --lw-bg-deep:        #020617;                  /* slate-950 */
  --lw-border:         #1e293b;                  /* slate-800 */
  --lw-border-strong:  #334155;                  /* slate-700 */

  /* Type — bumped from the original (label was 10px uppercase, hint was
   * 9px - both too small). Now: 11px labels, 12px hints, 14px body. */
  --lw-fg:        #f1f5f9;   /* slate-100 */
  --lw-fg-muted:  #cbd5e1;   /* slate-300 - upsized contrast vs previous slate-400 */
  --lw-fg-soft:   #94a3b8;   /* slate-400 */

  /* Shadow stack - heavier on dark surfaces so the panel sits clearly
   * above the page. */
  --lw-shadow-soft: 0 1px 2px rgba(0, 0, 0, 0.40), 0 8px 24px rgba(0, 0, 0, 0.30);
  --lw-shadow-lift: 0 1px 2px rgba(0, 0, 0, 0.50), 0 20px 56px rgba(0, 0, 0, 0.45);

  /* Motion. Two durations + the "rubber" curve Linear / Vercel use for
   * the soft-overshoot feel without any actual overshoot. */
  --lw-d-fast: 160ms;
  --lw-d-med:  240ms;
  --lw-ease:   cubic-bezier(0.16, 1, 0.3, 1);
}

.root { position: fixed; z-index: 2147483640; pointer-events: none; }
.root > * { pointer-events: auto; }

.bottom-right { bottom: 20px; right: 20px; }
.bottom-left  { bottom: 20px; left: 20px; }
.top-right    { top: 20px; right: 20px; }
.top-left     { top: 20px; left: 20px; }

/* ===========================================================
 * Pill — single visible surface for the widget
 * =========================================================== */

.pill {
  display: inline-flex;
  align-items: center;
  background: var(--lw-surface);
  -webkit-backdrop-filter: blur(20px) saturate(180%);
  backdrop-filter: blur(20px) saturate(180%);
  border: 1px solid var(--lw-border);
  border-radius: 999px;
  box-shadow: var(--lw-shadow-soft);
  overflow: hidden;
  transition:
    box-shadow var(--lw-d-med) var(--lw-ease),
    border-color var(--lw-d-med) var(--lw-ease),
    background var(--lw-d-med) var(--lw-ease),
    transform var(--lw-d-med) var(--lw-ease);
}
.pill:hover {
  box-shadow: var(--lw-shadow-lift);
  border-color: var(--lw-border-strong);
  transform: translateY(-1px);
}

.pill-toggle {
  appearance: none;
  border: 0;
  background: transparent;
  color: inherit;
  display: inline-flex;
  align-items: center;
  gap: 0;
  padding: 6px;          /* Always a perfect circle around the 28px icon. */
  cursor: pointer;
  font: inherit;
  border-radius: 999px;
}
.pill-toggle:focus-visible {
  outline: 2px solid var(--lw-accent-flat);
  outline-offset: 3px;
}

.pill-icon {
  width: 28px;
  height: 28px;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 28px;
  background: rgba(255, 255, 255, 0.06);
  transition:
    background var(--lw-d-fast) var(--lw-ease),
    transform var(--lw-d-fast) var(--lw-ease);
}
.pill-icon svg {
  width: 18px;
  height: 18px;
  fill: var(--lw-fg-muted);
  transition: fill var(--lw-d-fast) var(--lw-ease);
}

/* LL brand mark — stems use scaleY transform so they animate from
 * the bottom (feet stay anchored, the letterform stays legible). */
.pill-icon .ll-mark .ll-stem,
.pill-icon .ll-mark .ll-foot {
  transform-origin: 50% 100%;
  transition: transform var(--lw-d-fast) var(--lw-ease);
}
.pill-icon .ll-mark .ll-stem { transform-box: fill-box; }

/* Idle + active state: stems at a slightly trimmed "rest" height so the
 * mark reads as LL letters. Subtle independent wiggle hints at the
 * underlying soundwave nature without being noisy. */
.pill.active:not(.playing):not(.loading) .ll-stem-1 {
  animation: lw-ll-rest-1 4.2s ease-in-out infinite;
}
.pill.active:not(.playing):not(.loading) .ll-stem-2 {
  animation: lw-ll-rest-2 4.8s ease-in-out infinite;
}
@keyframes lw-ll-rest-1 {
  0%, 100% { transform: scaleY(1); }
  50%      { transform: scaleY(0.92); }
}
@keyframes lw-ll-rest-2 {
  0%, 100% { transform: scaleY(0.96); }
  50%      { transform: scaleY(1); }
}

/* Playing state: stems become a proper 2-bar audio visualiser. Two
 * different loops keep them out of sync so it doesn't read as a
 * synchronised "throb". */
.pill.playing .ll-stem-1 {
  animation: lw-ll-bar-1 0.9s ease-in-out infinite;
}
.pill.playing .ll-stem-2 {
  animation: lw-ll-bar-2 1.3s ease-in-out infinite;
}
@keyframes lw-ll-bar-1 {
  0%, 100% { transform: scaleY(1); }
  25%      { transform: scaleY(0.40); }
  60%      { transform: scaleY(0.85); }
}
@keyframes lw-ll-bar-2 {
  0%, 100% { transform: scaleY(0.55); }
  35%      { transform: scaleY(1); }
  70%      { transform: scaleY(0.32); }
}

/* Status label is screen-reader-only. The pill is a circle in every state;
 * sighted users get state via the icon animation (idle breathe, playing
 * pulse, loading dots). SR users still get a spoken label via aria-live. */
.pill-status {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

/* Active — circle stays a circle. Icon gets the brand gradient. */
.pill.active {
  background: var(--lw-surface);
  border-color: rgba(167, 139, 250, 0.30);
}
.pill.active .pill-icon {
  background: linear-gradient(135deg, var(--lw-accent-start), var(--lw-accent-end));
  box-shadow: 0 4px 12px rgba(167, 139, 250, 0.35);
}
.pill.active .pill-icon svg { fill: #fff; }

/* Idle-but-active: faint breathing glow so the widget feels alive
 * when waiting for a selection, without the loud pulse used during
 * playback. */
.pill.active:not(.playing):not(.loading) .pill-icon {
  animation: lw-breathe 3.6s ease-in-out infinite;
}
@keyframes lw-breathe {
  0%, 100% { box-shadow: 0 4px 12px rgba(167, 139, 250, 0.35); }
  50%      { box-shadow: 0 6px 18px rgba(167, 139, 250, 0.50); }
}

/* Playing — pulse the icon (more emphatic than breathe). */
.pill.playing .pill-icon {
  animation: lw-pulse 1.4s ease-in-out infinite;
}
@keyframes lw-pulse {
  0%, 100% { box-shadow: 0 0 0 0 rgba(167, 139, 250, 0.55); }
  50%      { box-shadow: 0 0 0 8px rgba(167, 139, 250, 0); }
}
.pill.playing .pill-status {
  background: linear-gradient(135deg, var(--lw-accent-start), var(--lw-accent-end));
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  color: transparent;
}

/* Loading — three staggered dots in place of the speaker icon (or a
 * translate glyph when translating, see below). */
.pill.loading .pill-icon { background: linear-gradient(135deg, var(--lw-accent-start), var(--lw-accent-end)); }
.pill.loading .pill-icon svg circle {
  fill: #fff;
  opacity: 0.4;
  animation: lw-dot 1s ease-in-out infinite;
}
.pill.loading .pill-icon svg circle:nth-child(1) { animation-delay: 0s; }
.pill.loading .pill-icon svg circle:nth-child(2) { animation-delay: 0.15s; }
.pill.loading .pill-icon svg circle:nth-child(3) { animation-delay: 0.30s; }
@keyframes lw-dot {
  0%, 80%, 100% { opacity: 0.4; transform: scale(1); }
  40%           { opacity: 1; transform: scale(1.2); }
}

/* Translating — translate glyph (Material icon) with a soft pulse so
 * the user can see it's an active operation, not a static label. */
.pill.loading .pill-icon.icon-translate svg path {
  fill: #fff;
  animation: lw-translate-pulse 1.6s ease-in-out infinite;
  transform-origin: center;
  transform-box: fill-box;
}
@keyframes lw-translate-pulse {
  0%, 100% { opacity: 0.85; transform: scale(0.94); }
  50%      { opacity: 1;    transform: scale(1.02); }
}
.pill.loading .pill-status {
  background: linear-gradient(135deg, var(--lw-accent-start), var(--lw-accent-end));
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  color: transparent;
}

/* Gear button — slides into view when the pill goes active. */
.pill-gear {
  appearance: none;
  border: 0;
  background: transparent;
  cursor: pointer;
  width: 0;
  height: 34px;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--lw-fg-soft);
  opacity: 0;
  overflow: hidden;
  border-left: 0 solid transparent;
  transition:
    width var(--lw-d-med) var(--lw-ease),
    opacity var(--lw-d-fast) var(--lw-ease),
    color var(--lw-d-fast) var(--lw-ease),
    background var(--lw-d-fast) var(--lw-ease);
}
.pill-gear svg { width: 15px; height: 15px; fill: currentColor; }
.pill-gear:hover { color: var(--lw-accent-flat); background: var(--lw-accent-soft); }
.pill-gear:focus-visible { outline: 2px solid var(--lw-accent-flat); outline-offset: -2px; }
.pill.active .pill-gear {
  width: 38px;
  opacity: 1;
  border-left: 1px solid var(--lw-border);
}

/* ===========================================================
 * Settings panel — opens above the pill
 * =========================================================== */

.settings {
  position: absolute;
  bottom: calc(100% + 12px);
  right: 0;
  width: 320px;
  background: var(--lw-surface);
  -webkit-backdrop-filter: blur(24px) saturate(180%);
  backdrop-filter: blur(24px) saturate(180%);
  border: 1px solid var(--lw-border);
  border-radius: 18px;
  box-shadow: var(--lw-shadow-lift);
  font-size: 14px;
  color: var(--lw-fg);
  opacity: 0;
  transform: translateY(8px) scale(0.97);
  pointer-events: none;
  transition:
    opacity var(--lw-d-med) var(--lw-ease),
    transform var(--lw-d-med) var(--lw-ease);
}
.settings.open {
  opacity: 1;
  transform: translateY(0) scale(1);
  pointer-events: auto;
}

.settings-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 18px 12px;
}
.settings-header strong {
  font-size: 15px;
  font-weight: 600;
  letter-spacing: -0.01em;
  background: linear-gradient(135deg, var(--lw-accent-start), var(--lw-accent-end));
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  color: transparent;
}

.settings-close {
  appearance: none;
  border: 0;
  background: transparent;
  cursor: pointer;
  padding: 4px;
  border-radius: 6px;
  color: var(--lw-fg-soft);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: background var(--lw-d-fast) var(--lw-ease), color var(--lw-d-fast) var(--lw-ease);
}
.settings-close:hover { background: rgba(255, 255, 255, 0.06); color: var(--lw-fg); }
.settings-close svg { width: 14px; height: 14px; fill: currentColor; }

.settings-body {
  padding: 6px 18px 16px;
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.settings-row {
  display: flex;
  flex-direction: column;
  gap: 7px;
  transition: opacity var(--lw-d-fast) var(--lw-ease);
}
.settings-row-toggle {
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}
.settings-row-disabled { opacity: 0.4; pointer-events: none; }

.settings-label {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--lw-fg-muted);
  display: inline-flex;
  flex-direction: column;
  gap: 2px;
}

.settings-badge {
  display: inline-block;
  font-size: 9px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  background: var(--lw-accent-soft);
  color: var(--lw-accent-flat);
  padding: 2px 6px;
  border-radius: 4px;
}

.settings select {
  appearance: none;
  width: 100%;
  font: inherit;
  font-size: 14px;
  font-weight: 500;
  padding: 10px 36px 10px 14px;
  border: 1px solid var(--lw-border);
  border-radius: 10px;
  background-color: var(--lw-bg-deep);
  color: var(--lw-fg);
  background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 12 12'><path fill='%23cbd5e1' d='M2.5 4.5L6 8l3.5-3.5z'/></svg>");
  background-repeat: no-repeat;
  background-position: right 12px center;
  cursor: pointer;
  transition: border-color var(--lw-d-fast) var(--lw-ease), background-color var(--lw-d-fast) var(--lw-ease);
}
.settings select:hover { border-color: var(--lw-border-strong); }
.settings select:focus {
  outline: none;
  border-color: var(--lw-accent-flat);
  box-shadow: 0 0 0 3px var(--lw-accent-soft);
}
.settings select:disabled { background-color: rgba(255,255,255,0.02); cursor: not-allowed; }

/* Segmented speed picker — sliding-active-pill style. The 'sel' button
 * sits on a translucent white rounded background; the surrounding track
 * is a slightly inset gray. */
.settings-speed {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 2px;
  background: var(--lw-bg-deep);
  border: 1px solid var(--lw-border);
  border-radius: 10px;
  padding: 3px;
  position: relative;
}
.settings-speed button {
  appearance: none;
  border: 0;
  background: transparent;
  font: inherit;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: -0.005em;
  color: var(--lw-fg-muted);
  padding: 8px 0;
  border-radius: 7px;
  cursor: pointer;
  position: relative;
  z-index: 1;
  transition:
    color var(--lw-d-fast) var(--lw-ease),
    transform 80ms var(--lw-ease);
}
.settings-speed button:hover { color: var(--lw-fg); }
.settings-speed button:active { transform: scale(0.96); }
.settings-speed button.sel {
  background: rgba(255, 255, 255, 0.06);
  color: var(--lw-accent-flat);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.40), 0 0 0 1px rgba(255, 255, 255, 0.06);
}

/* Toggle — 36×20 with smooth thumb slide. */
.settings-translate, .settings-karaoke {
  appearance: none;
  width: 36px;
  height: 20px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.14);
  position: relative;
  cursor: pointer;
  transition: background var(--lw-d-fast) var(--lw-ease);
  flex: 0 0 36px;
}
.settings-translate::after, .settings-karaoke::after {
  content: '';
  position: absolute;
  top: 2px; left: 2px;
  width: 16px; height: 16px;
  border-radius: 999px;
  background: #fff;
  box-shadow: 0 1px 3px rgba(0,0,0,0.20);
  transition: transform var(--lw-d-fast) var(--lw-ease);
}
.settings-translate:checked, .settings-karaoke:checked {
  background: linear-gradient(135deg, var(--lw-accent-start), var(--lw-accent-end));
}
.settings-translate:checked::after, .settings-karaoke:checked::after { transform: translateX(16px); }
.settings-translate:disabled, .settings-karaoke:disabled { cursor: not-allowed; opacity: 0.5; }

.settings-hint-inline {
  display: block;
  font-size: 11px;
  font-weight: 400;
  color: var(--lw-fg-soft);
  text-transform: none;
  letter-spacing: 0;
  margin-top: 2px;
}

.settings-footer {
  padding: 14px 18px 16px;
  border-top: 1px solid var(--lw-border);
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.settings-hint {
  font-size: 12px;
  color: var(--lw-fg-soft);
  line-height: 1.55;
}

/* Powered-by credit. Links back to capsiynau.com so every Llais user
 * has a visible path to the parent product, mirroring the landing-page
 * 'Powered by Capsiynau' attribution shipped in PR #9. */
.settings-credit {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 11px;
  color: var(--lw-fg-soft);
  text-decoration: none;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  font-weight: 500;
  transition: color var(--lw-d-fast) var(--lw-ease);
}
.settings-credit strong {
  font-weight: 700;
  letter-spacing: -0.01em;
  text-transform: none;
  font-size: 12px;
  background: linear-gradient(135deg, var(--lw-accent-start), var(--lw-accent-end));
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  color: transparent;
}
.settings-credit:hover { color: var(--lw-fg-muted); }

/* Read-along now happens in-page (host DOM) — see karaoke.ts which
 * injects its own scoped stylesheet into document.head. The corner
 * panel was removed because users found their eye jumping from the
 * selection to the panel distracting; in-page highlighting stays on
 * the reading line. */

/* ===========================================================
 * Language toggle (EN / CY segmented)
 * =========================================================== */

.settings-langtoggle {
  display: inline-flex;
  gap: 2px;
  background: var(--lw-bg-deep);
  border: 1px solid var(--lw-border);
  border-radius: 8px;
  padding: 2px;
}
.settings-langtoggle button {
  appearance: none;
  border: 0;
  background: transparent;
  font: inherit;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  color: var(--lw-fg-muted);
  padding: 4px 10px;
  border-radius: 6px;
  cursor: pointer;
  transition:
    color var(--lw-d-fast) var(--lw-ease),
    background var(--lw-d-fast) var(--lw-ease);
}
.settings-langtoggle button:hover { color: var(--lw-fg); }
.settings-langtoggle button.sel {
  background: rgba(255, 255, 255, 0.06);
  color: var(--lw-accent-flat);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.40), 0 0 0 1px rgba(255, 255, 255, 0.06);
}

/* ===========================================================
 * Inline selection bubble
 * =========================================================== */

/* Bubble — icon-only floating indicator anchored to the selection while
 * loading. Always shows the animated LL brand mark; the status label is
 * kept in the DOM for screen readers but visually hidden. */
.bubble {
  position: fixed;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 6px;
  background: rgba(20, 18, 38, 0.90);
  -webkit-backdrop-filter: blur(12px) saturate(180%);
  backdrop-filter: blur(12px) saturate(180%);
  color: #fff;
  border-radius: 999px;
  box-shadow: 0 6px 20px rgba(20, 18, 38, 0.30);
  opacity: 0;
  transform: translateY(3px) scale(0.96);
  pointer-events: none;
  transition:
    opacity var(--lw-d-fast) var(--lw-ease),
    transform var(--lw-d-fast) var(--lw-ease);
  will-change: opacity, transform;
}
.bubble.open {
  opacity: 1;
  transform: translateY(0) scale(1);
}
.bubble-icon {
  display: inline-flex;
  width: 22px; height: 22px;
  align-items: center; justify-content: center;
}
.bubble-icon svg { width: 18px; height: 18px; fill: #fff; }

/* Animate the LL mark's stems while the bubble is open — same audio-bar
 * loop the pill uses during playback, so the loading state reads as
 * 'Llais is working' rather than a static glyph. */
.bubble .ll-mark .ll-stem,
.bubble .ll-mark .ll-foot {
  transform-origin: 50% 100%;
  transition: transform var(--lw-d-fast) var(--lw-ease);
}
.bubble .ll-mark .ll-stem { transform-box: fill-box; }
.bubble.open .ll-stem-1 { animation: lw-ll-bar-1 0.9s ease-in-out infinite; }
.bubble.open .ll-stem-2 { animation: lw-ll-bar-2 1.3s ease-in-out infinite; }

/* SR-only label - kept in DOM so aria-live announces 'Speaking…' or
 * 'Translating…' but hidden visually so the bubble is purely an icon. */
.bubble-label {
  position: absolute;
  width: 1px; height: 1px;
  padding: 0; margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

/* Reading-mode slot. Visible only when .bubble.reading is set (i.e.
 * the translated-audio karaoke path). Default state hides it so the
 * loading-mode icon chip behaviour stays unchanged. */
.bubble-reading {
  display: none;
}
.bubble.reading {
  /* Expand from the icon chip to a multi-line text card next to the
   * user's selection. Same backdrop + brand colour family so it reads
   * as the same widget, just doing a different job. */
  border-radius: 12px;
  padding: 10px 14px;
  max-width: 360px;
  font: 500 14px/1.45 system-ui, -apple-system, 'Segoe UI', sans-serif;
  letter-spacing: 0.005em;
  text-align: left;
  white-space: normal;
  /* Translated text is the primary content in reading mode; tuck the
   * brand mark out of view so it doesn't compete with the prose. The
   * SR label is already visually-hidden via .bubble-label. */
}
.bubble.reading .bubble-icon {
  display: none;
}
.bubble.reading .bubble-reading {
  display: inline;
}

/* Karaoke span styles for the in-bubble path.
 *
 * The InPageHighlighter wraps each spoken word in a .lw-w span (or .lw-p
 * for punctuation) and toggles .active on the current one. For in-page
 * selections it injects matching rules into document.head — but the
 * bubble lives inside the widget shadow root, and those host-head
 * rules can't cross the shadow boundary, so spans wrapped INSIDE the
 * bubble would render unstyled (Codex on PR #55).
 *
 * Mirror the rules here so translated-audio karaoke is visible inside
 * the bubble. Kept visually identical to the host-head version so the
 * UX feels the same across both paths. Specificity stays low because
 * we're in shadow scope — no host-page rules to override.
 */
.bubble .lw-w,
.bubble .lw-p {
  background: rgba(167, 139, 250, 0.20);
  border-radius: 3px;
  padding: 1px 2px;
  margin: 0 -1px;
  transition: color 140ms cubic-bezier(0.16, 1, 0.3, 1),
              background 140ms cubic-bezier(0.16, 1, 0.3, 1),
              text-shadow 140ms cubic-bezier(0.16, 1, 0.3, 1);
}
.bubble .lw-w.active {
  color: #ffffff;
  background: linear-gradient(135deg, #a78bfa, #818cf8);
  text-shadow: 0 0 14px rgba(167, 139, 250, 0.45);
  font-weight: 600;
}
@media (prefers-reduced-motion: reduce) {
  .bubble .lw-w,
  .bubble .lw-p { transition: none; }
}

/* ===========================================================
 * Toast
 * =========================================================== */

.toast {
  position: absolute;
  bottom: calc(100% + 12px);
  right: 0;
  padding: 9px 13px;
  background: rgba(20, 18, 38, 0.92);
  -webkit-backdrop-filter: blur(12px) saturate(180%);
  backdrop-filter: blur(12px) saturate(180%);
  color: #fff;
  font-size: 12px;
  letter-spacing: -0.005em;
  border-radius: 10px;
  max-width: 280px;
  box-shadow: 0 6px 20px rgba(20, 18, 38, 0.30);
  animation: lw-fade 240ms var(--lw-ease) both;
}
.toast.error { background: rgba(185, 28, 28, 0.92); }
@keyframes lw-fade {
  from { opacity: 0; transform: translateY(4px); }
  to   { opacity: 1; transform: translateY(0); }
}

/* Note: the widget is force-dark on every host page. The previous
 * @media (prefers-color-scheme: dark) override was removed when the
 * palette was baked into :host - the Llais brand reads as Llais
 * regardless of whether the host page is light or dark, matching the
 * 'Powered by Capsiynau' attribution + the dark Capsiynau landing. */

/* ===========================================================
 * Reduced-motion
 * =========================================================== */

@media (prefers-reduced-motion: reduce) {
  .pill, .pill-toggle, .pill-status, .pill-gear, .pill-icon,
  .settings, .toast, .settings-speed button, .settings-langtoggle button,
  .settings-translate, .settings-translate::after,
  .settings-karaoke, .settings-karaoke::after,
  .bubble, .bubble svg circle, .pill.loading .pill-icon svg circle,
  .ll-mark .ll-stem, .ll-mark .ll-foot {
    animation: none !important;
    transition: none !important;
  }
}
`;
